import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Image, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, Spinner, Text} from '@chakra-ui/react';
import {agentGet} from '../../utils/apis/agentApi';

/** Load private images through the authenticated API only when near the viewport.
 * Never put the agent token, Meta URL, or image bytes in browser storage. */
export default function ConversationImage({phone, messageId}: {phone: string; messageId: string}) {
  const element = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [retry, setRetry] = useState(0);
  const [src, setSrc] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!element.current || typeof IntersectionObserver === 'undefined') {setVisible(true); return;}
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {setVisible(true); observer.disconnect();}
    }, {rootMargin: '200px'});
    observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    let active = true;
    setSrc(''); setError(''); setOpen(false);
    if (visible) {
      agentGet<{data_url: string}>(`api/conversation/${encodeURIComponent(phone)}/images/${encodeURIComponent(messageId)}`)
        .then(result => {
          if (!active) return;
          if (result.ok && /^data:image\/(jpeg|png|webp|gif);base64,[A-Za-z0-9+/=]+$/.test(result.data?.data_url || '')) {
            setSrc(result.data!.data_url);
          } else {
            setError(result.status === 404 ? 'This photo is no longer available.' :
              result.status === 415 ? 'This photo format cannot be previewed.' :
              'Photo could not be loaded. Older WhatsApp photos may no longer be available.');
          }
        }).catch(() => {if (active) setError('Photo could not be loaded. Please retry.');});
    }
    return () => {active = false;};
  }, [phone, messageId, visible, retry]);
  return <Box ref={element} minH="80px" minW="160px" my={2}>
    {src ? <Button variant="unstyled" height="auto" display="block" maxW="100%"
      aria-label="Open photo" onClick={() => setOpen(true)}>
      <Image src={src} alt="Photo sent in this conversation" maxW="280px" width="100%" maxH="240px"
        objectFit="contain" borderRadius="md" onError={() => {setSrc(''); setError('This photo could not be displayed.');}} />
      <Text as="span" display="block" fontSize="xs" mt={1}>Click to enlarge</Text>
    </Button> : error ? <Box role="status"><Text fontSize="sm">{error}</Text>
      <Button size="xs" mt={2} onClick={() => setRetry(value => value + 1)}>Retry photo</Button></Box> :
      <Box fontSize="sm">{visible && <Spinner size="xs" mr={2} />}Loading photo…</Box>}
    <Modal isOpen={open && !!src} onClose={() => setOpen(false)} size="4xl" isCentered>
      <ModalOverlay /><ModalContent><ModalHeader>Conversation photo</ModalHeader><ModalCloseButton />
        <ModalBody pb={5}><Image src={src} alt="Full-size conversation photo" maxH="80vh" mx="auto" objectFit="contain" /></ModalBody>
      </ModalContent>
    </Modal>
  </Box>;
}
